// Updated: 02-04-2026
// Changes:
// - Card "Remark:" label renamed to "Feedback:"
// - "Note:" block removed (Notes merged into Feedback in form)
// - "Created by" changed to "Scheduled by"
// - Action button "Remark" renamed to "Feedback"
// - "Note" action button removed
// - Modal title updated to match
// - Visit type badge (Agent / Direct) shown on card

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, User, Home, UserCheck, Users, History, Pencil, Trash2, MessageSquare, RefreshCw } from "lucide-react";
import VisitActivityLog from "./VisitActivityLog";
import { formatDateTime, toDateTimeLocal } from "../../utils/datetime";
import "../../css/sitevisit/sitevisitcard.css";

export default function SiteVisitCard({ visit, lead, property, index, isAdmin, onSave, onEdit, onDelete }) {
  const [activeAction, setActiveAction] = useState(null);
  const [value, setValue] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [showLog, setShowLog] = useState(false);

  const closeModal = () => { setActiveAction(null); setValue(""); setRescheduleDate(""); };

  const isRescheduling = activeAction === "status" && value === "rescheduled";

  const handleSave = () => {
    if (isRescheduling && !rescheduleDate) {
      alert("Please select the new date & time for the rescheduled visit.");
      return;
    }
    const extra = isRescheduling
      ? { scheduled_date: new Date(rescheduleDate).toISOString() }
      : {};
    onSave(activeAction, value, visit._id, extra);
    closeModal();
  };

  const isAgent = visit.visit_type === "agent";

  return (
    <>
      <motion.div
        className="site-visit-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
      >
        {/* HEADER */}
        <div className="card-header">
          <div className="header-left">
            <div className="icon-circle">
              <Home size={18} />
            </div>
            <div>
              <h3>{property?.propertyName}</h3>
              <div className="row muted">
                <MapPin size={14} />
                <span>{property?.location}</span>
              </div>
            </div>
          </div>

          {/* Visit type badge */}
          <span className={`visit-type-badge ${isAgent ? "badge-agent" : "badge-direct"}`}>
            {isAgent ? <><UserCheck size={11} /> Agent</> : <><Users size={11} /> Direct</>}
          </span>
        </div>

        {/* BODY */}
        <div className="card-content">
          <div className="row">
            <User size={14} />
            <span>{lead?.full_name}</span>
          </div>

          {/* Show agent email if agent visit */}
          {isAgent && visit.agent_email && (
            <div className="row muted">
              <UserCheck size={13} />
              <span>{visit.agent_email}</span>
            </div>
          )}

          <div className="row">
            <Calendar size={14} />
            <span>{formatDateTime(visit.scheduled_date)}</span>
          </div>

          {/* STATUS */}
          <div className="status-wrapper">
            <span className={`status-pill ${visit.status}`}>
              {visit.status}
            </span>
          </div>

          {/* FEEDBACK shown for completed visits */}
          {visit?.feedback && (
            <div className="info-block">
              <strong>Feedback:</strong>
              <p>{visit.feedback}</p>
            </div>
          )}

          {/* REMARK shown for non-completed visits */}
          {visit?.remark && (
            <div className="info-block">
              <strong>Remarks:</strong>
              <p>{visit.remark}</p>
            </div>
          )}

          {/* ACTIONS */}
          <div className="action-row">
            <button
              onClick={() => {
                setActiveAction("feedback");
                setValue(visit.feedback || "");
              }}
            >
              <MessageSquare size={13} /> Feedback
            </button>

            <button
              onClick={() => {
                setActiveAction("status");
                setValue(visit.status || "");
                setRescheduleDate(toDateTimeLocal(visit.scheduled_date));
              }}
            >
              <RefreshCw size={13} /> Status
            </button>

            {onEdit && (
              <button onClick={onEdit}>
                <Pencil size={13} /> Edit
              </button>
            )}

            {onDelete && (
              <button onClick={onDelete}>
                <Trash2 size={13} /> Delete
              </button>
            )}

            <button onClick={() => setShowLog(true)}>
              <History size={13} /> Activity
            </button>
          </div>

          {/* FOOTER — "Scheduled by" instead of "Created by" */}
          {isAdmin && visit.createdByEmail && (
            <div className="created-by">
              Scheduled by: <strong>{visit.createdByEmail}</strong>
            </div>
          )}
        </div>
      </motion.div>

      {/* MODAL */}
      {activeAction && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h4>
              {activeAction === "status" ? "Update Status" : "Add Feedback"}
            </h4>

            {activeAction === "status" ? (
              <>
                <select value={value} onChange={(e) => setValue(e.target.value)}>
                  <option value="" disabled>— Select Status —</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>

                {value === "rescheduled" && (
                  <div className="reschedule-field">
                    <label>New Date &amp; Time *</label>
                    <input
                      type="datetime-local"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                    />
                  </div>
                )}
              </>
            ) : (
              <textarea
                placeholder="Enter feedback..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            )}

            <div className="modal-actions">
              <button className="cancel" onClick={closeModal}>Cancel</button>
              <button className="save" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ACTIVITY LOG MODAL */}
      {showLog && (
        <VisitActivityLog log={visit.activityLog || []} onClose={() => setShowLog(false)} />
      )}
    </>
  );
}